import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RefreshCw, Play, Send, Check, X, Link2, UserPlus, Trash2 } from 'lucide-react';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import Tabs from './shared/Tabs';
import Toast from './shared/Toast';
import ConfirmModal from './shared/ConfirmModal';
import accountsAgentService from '../services/AccountsAgentService';
import { formatDateTime } from '../utils/datetime';
import './AccountsAgentPage.css';

// Admin > Billing > Accounts Agent (code-accounts).
//
// The agent PREPARES — reports, reconciliation, and (from Phase 2) invoices, chasers and Stripe payment
// postings — and a named approver DECIDES. This screen is where both happen: what each job last concluded,
// the approval queue, every run with its figures and commentary, a read-only "ask", and (superadmin) the
// QuickBooks connection, the approvers and each job's mode and automatic-action limit.

const TABS = ['overview', 'approvals', 'runs', 'contracts', 'onboarding', 'vat', 'ask', 'settings'];
const EMPTY_CONTRACT = { qboCustomerId: '', customerName: '', title: '', qboItemId: '', description: '', amount: '', currency: 'EUR', frequency: 'monthly', startDate: '', endDate: '', paymentTermsDays: 30, renewalNoticeDays: 60 };
const EMPTY_ONBOARDING = { companyName: '', billingEmail: '', contactName: '', addressLine1: '', addressLine2: '', city: '', postalCode: '', countryCode: 'IE', vatNumber: '' };
const VAT_BOXES = ['T1', 'T2', 'T3', 'T4', 'E1', 'E2', 'ES1', 'ES2'];
const VAT_ROLE_LABELS = {
    domestic: 'Irish VAT (sales → T1, purchases → T2)', eu_goods_supply: 'EU supply of goods (E1)', eu_services_supply: 'EU supply of services (ES1)',
    eu_goods_acquisition: 'EU acquisition of goods (E2, self-assessed)', eu_services_acquisition: 'EU acquisition of services (ES2, self-assessed)',
    reverse_charge: 'Domestic reverse charge (self-assessed)', exempt: 'Exempt (not on the VAT3)', ignore: 'Ignore',
};
const money = (n, c = 'EUR') => (n === null || n === undefined ? '—'
    : new Intl.NumberFormat('en-IE', { style: 'currency', currency: c || 'EUR' }).format(Number(n)));

const StatusChip = ({ status }) => (
    <span className={`aag-chip aag-chip-${String(status || '').toLowerCase()}`}>{status}</span>
);

// Polls one run until it finishes — "run now" and "ask" are asynchronous (the agent may take minutes).
const usePollRun = () => {
    const timer = useRef(null);
    useEffect(() => () => clearTimeout(timer.current), []);
    return useCallback((runId, onDone) => {
        const tick = async () => {
            try {
                const r = await accountsAgentService.run(runId);
                if (r.status === 'RUNNING') { timer.current = setTimeout(tick, 3000); return; }
                onDone(r);
            } catch (e) { onDone({ status: 'FAILED', error: e.message }); }
        };
        timer.current = setTimeout(tick, 2000);
    }, []);
};

const csvCell = (v) => { const t = v === null || v === undefined ? '' : String(v); return /[",\r\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t; };

// A LIST A RUN PRODUCED (output.table: { title, fileName, columns: [{ key, label, numeric }], rows }) — e.g. the
// Invoice list. Filterable by status and by text; the download is the filtered rows.
const RunTable = ({ table }) => {
    const [status, setStatus] = useState('');
    const [text, setText] = useState('');
    const hasStatus = table.columns.some(c => c.key === 'status');
    const statuses = hasStatus ? [...new Set(table.rows.map(r => r.status))].sort() : [];
    const needle = text.trim().toLowerCase();
    const rows = table.rows.filter(r => (!status || r.status === status)
        && (!needle || table.columns.some(c => String(r[c.key] ?? '').toLowerCase().includes(needle))));
    const download = () => {
        const csv = [table.columns.map(c => csvCell(c.label)).join(',')]
            .concat(rows.map(r => table.columns.map(c => csvCell(r[c.key])).join(','))).join('\r\n');
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
        const a = document.createElement('a'); a.href = url; a.download = table.fileName || 'list.csv'; a.click(); URL.revokeObjectURL(url);
    };
    return (
        <details open>
            <summary>{table.title} ({rows.length}{rows.length !== table.rows.length ? ` of ${table.rows.length}` : ''})</summary>
            <div className="aag-toolbar">
                {hasStatus && (
                    <select value={status} onChange={e => setStatus(e.target.value)}>
                        <option value="">All statuses</option>
                        {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                )}
                <input type="search" placeholder="Filter" value={text} onChange={e => setText(e.target.value)} />
                <Button variant="secondary" onClick={download}>Download CSV</Button>
            </div>
            <div className="aag-table-scroll">
                <table className="aag-table">
                    <thead><tr>{table.columns.map(c => <th key={c.key} className={c.numeric ? 'aag-num' : undefined}>{c.label}</th>)}</tr></thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={r.qboId || i}>
                                {table.columns.map(c => (
                                    <td key={c.key} className={c.numeric ? 'aag-num' : undefined}>
                                        {c.key === 'status' ? <StatusChip status={r.status} />
                                            : c.numeric ? money(r[c.key], r.currency) : (r[c.key] ?? '')}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </details>
    );
};

const RunDetail = ({ run, onClose }) => (
    <div className="aag-modal-backdrop" onClick={onClose}>
        <div className="aag-modal" onClick={e => e.stopPropagation()}>
            <div className="aag-modal-head">
                <h3>{run.jobKey} — run {run.id}</h3>
                <button type="button" className="aag-icon-btn" onClick={onClose} aria-label="Close"><X size={18} /></button>
            </div>
            <p className="aag-muted">{formatDateTime(run.startedAt)} · <StatusChip status={run.status} /></p>
            {run.error && <div className="aag-error">{run.error}</div>}
            {run.output && run.output.commentary && <div className="aag-commentary">{run.output.commentary}</div>}
            {run.output && run.output.answer && <div className="aag-commentary">{run.output.answer}</div>}
            {run.output && run.output.figures && (
                <details open>
                    <summary>Figures</summary>
                    <pre className="aag-pre">{JSON.stringify(run.output.figures, null, 2)}</pre>
                </details>
            )}
            {run.output && run.output.table && <RunTable table={run.output.table} />}
            {run.output && run.output.lookups && run.output.lookups.length > 0 && (
                <details>
                    <summary>What it looked up ({run.output.lookups.length})</summary>
                    <pre className="aag-pre">{JSON.stringify(run.output.lookups, null, 2)}</pre>
                </details>
            )}
        </div>
    </div>
);

export default function AccountsAgentPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');
    const tab = TABS.includes(tabParam) ? tabParam : 'overview';
    const setTab = (t) => { const next = new URLSearchParams(searchParams); next.set('tab', t); setSearchParams(next, { replace: true }); };

    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);
    const [runs, setRuns] = useState([]);
    const [actions, setActions] = useState([]);
    const [actionStatus, setActionStatus] = useState('PROPOSED');
    const [openRun, setOpenRun] = useState(null);
    const [question, setQuestion] = useState('');
    const [asking, setAsking] = useState(false);
    const [answer, setAnswer] = useState(null);
    const [approvers, setApprovers] = useState([]);
    const [userQuery, setUserQuery] = useState('');
    const [userHits, setUserHits] = useState([]);
    const [confirm, setConfirm] = useState(null);   // { title, message, onConfirm, danger }
    const [settingsRows, setSettingsRows] = useState([]);
    const [settingsDraft, setSettingsDraft] = useState({});
    const [qboAccounts, setQboAccounts] = useState([]);
    const [vatPeriods, setVatPeriods] = useState([]);
    const [vatOpen, setVatOpen] = useState(null);
    const [vatFiled, setVatFiled] = useState({ rosReference: '', filedOn: '' });
    const [vatRates, setVatRates] = useState(null);
    const [contracts, setContracts] = useState([]);
    const [contractForm, setContractForm] = useState(null);       // null | { ...fields, id? }
    const [customerQuery, setCustomerQuery] = useState('');
    const [customerHits, setCustomerHits] = useState([]);
    const [qboItems, setQboItems] = useState([]);
    const [onboardingRows, setOnboardingRows] = useState([]);
    const [onboardingForm, setOnboardingForm] = useState(null);
    const pollRun = usePollRun();

    const say = (message, kind = 'success') => setToast({ message, kind });

    const loadOverview = useCallback(async () => {
        setLoading(true);
        try { setOverview(await accountsAgentService.overview()); }
        catch (e) { setToast({ message: `Could not load the accounts agent: ${e.message}`, kind: 'error' }); }
        finally { setLoading(false); }
    }, []);
    const loadRuns = useCallback(async () => { try { setRuns(await accountsAgentService.runs()); } catch (e) { setToast({ message: e.message, kind: 'error' }); } }, []);
    const loadActions = useCallback(async () => { try { setActions(await accountsAgentService.actions(actionStatus)); } catch (e) { setToast({ message: e.message, kind: 'error' }); } }, [actionStatus]);
    const loadApprovers = useCallback(async () => { try { setApprovers(await accountsAgentService.approvers()); } catch (e) { setToast({ message: e.message, kind: 'error' }); } }, []);

    useEffect(() => { loadOverview(); }, [loadOverview]);
    useEffect(() => { if (tab === 'runs') loadRuns(); }, [tab, loadRuns]);
    useEffect(() => { if (tab === 'approvals') loadActions(); }, [tab, loadActions]);
    const loadSettings = useCallback(async () => {
        try {
            const rows = await accountsAgentService.settings();
            setSettingsRows(rows);
            setSettingsDraft(Object.fromEntries(rows.map(r => [r.settingKey, r.value || ''])));
        } catch (e) { setToast({ message: e.message, kind: 'error' }); }
        // The chart of accounts only loads once QuickBooks is connected; a failure here is not an error to show.
        try { setQboAccounts(await accountsAgentService.qboAccounts()); } catch (e) { setQboAccounts([]); }
    }, []);
    useEffect(() => { if (tab === 'settings') { loadApprovers(); loadSettings(); } }, [tab, loadApprovers, loadSettings]);
    const loadVat = useCallback(async () => {
        try { setVatPeriods(await accountsAgentService.vatPeriods()); } catch (e) { setToast({ message: e.message, kind: 'error' }); }
        try { setVatRates(await accountsAgentService.vatRates()); } catch (e) { setVatRates({ error: e.response?.data?.error?.message || e.message }); }
    }, []);
    useEffect(() => { if (tab === 'vat') loadVat(); }, [tab, loadVat]);
    const loadContracts = useCallback(async () => {
        try { setContracts(await accountsAgentService.contracts()); } catch (e) { setToast({ message: e.message, kind: 'error' }); }
    }, []);
    const loadOnboarding = useCallback(async () => {
        try { setOnboardingRows(await accountsAgentService.onboarding()); } catch (e) { setToast({ message: e.message, kind: 'error' }); }
    }, []);
    useEffect(() => { if (tab === 'contracts') loadContracts(); }, [tab, loadContracts]);
    useEffect(() => { if (tab === 'onboarding') loadOnboarding(); }, [tab, loadOnboarding]);
    const openContractForm = async (c) => {
        setContractForm(c ? { ...c } : { ...EMPTY_CONTRACT });
        setCustomerHits([]); setCustomerQuery('');
        if (!qboItems.length) { try { setQboItems(await accountsAgentService.qboItems()); } catch (e) { setToast({ message: e.response?.data?.error?.message || e.message, kind: 'error' }); } }
    };
    const saveContract = async () => {
        const f = contractForm;
        try {
            if (f.id) {
                const { id, ...changes } = f;
                await accountsAgentService.updateContract(id, changes);
            } else {
                await accountsAgentService.createContract(f);
            }
            say('Contract saved'); setContractForm(null); loadContracts();
        } catch (e) { say(e.response?.data?.error?.message || e.message, 'error'); }
    };
    const openVat = async (id) => { try { setVatOpen(await accountsAgentService.vatPeriod(id)); setVatFiled({ rosReference: '', filedOn: '' }); } catch (e) { setToast({ message: e.message, kind: 'error' }); } };
    const downloadWorkingFile = async (id) => {
        try {
            const { fileName, csv } = await accountsAgentService.vatWorkingFile(id);
            const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
            const a = document.createElement('a'); a.href = url; a.download = fileName; a.click(); URL.revokeObjectURL(url);
        } catch (e) { setToast({ message: e.message, kind: 'error' }); }
    };

    const isSuperAdmin = !!(overview && overview.me && overview.me.isSuperAdmin);
    const isApprover = !!(overview && overview.me && overview.me.isApprover);

    const runNow = async (jobKey) => {
        try {
            const { runId } = await accountsAgentService.runJob(jobKey);
            say(`Started ${jobKey} (run ${runId})`, 'info');
            pollRun(runId, (r) => { say(`${jobKey}: ${r.status === 'SUCCEEDED' ? r.summary : `failed — ${r.error}`}`, r.status === 'SUCCEEDED' ? 'success' : 'error'); loadOverview(); });
        } catch (e) { say(e.response?.data?.error?.message || e.message, 'error'); }
    };

    const openRunDetail = async (id) => {
        try { setOpenRun(await accountsAgentService.run(id)); } catch (e) { say(e.message, 'error'); }
    };

    const ask = async () => {
        if (!question.trim()) return;
        setAsking(true); setAnswer(null);
        try {
            const { runId } = await accountsAgentService.ask(question.trim());
            pollRun(runId, (r) => { setAsking(false); setAnswer(r); });
        } catch (e) { setAsking(false); say(e.response?.data?.error?.message || e.message, 'error'); }
    };

    const decide = (a, approve) => setConfirm({
        title: approve ? 'Approve this action?' : 'Reject this action?',
        message: `${a.actionType}${a.customerName ? ` for ${a.customerName}` : ''}${a.amount != null ? ` — ${money(a.amount, a.currency)}` : ''}. ${approve ? 'It will be carried out exactly as shown.' : 'Nothing will be done.'}`,
        danger: !approve,
        onConfirm: async () => {
            setConfirm(null);
            try {
                if (approve) {
                    const r = await accountsAgentService.approve(a.id);
                    if (r && r.error) say(`Approved, but it failed: ${r.error}`, 'error');
                    else say(r && r.executed ? 'Approved and done' : 'Approved');
                } else {
                    await accountsAgentService.reject(a.id, 'Rejected on the Accounts Agent screen');
                    say('Rejected');
                }
                loadActions(); loadOverview();
            } catch (e) { say(e.response?.data?.error?.message || e.message, 'error'); }
        },
    });

    const setJob = async (jobKey, changes) => {
        try { await accountsAgentService.setJob(jobKey, changes); say('Saved'); loadOverview(); }
        catch (e) { say(e.response?.data?.error?.message || e.message, 'error'); }
    };

    const connectQuickBooks = async () => {
        try { const { url } = await accountsAgentService.connectQuickBooks(); window.open(url, '_blank', 'noopener'); }
        catch (e) { say(e.response?.data?.error?.message || e.message, 'error'); }
    };

    const searchUsers = async () => {
        try { setUserHits(await accountsAgentService.searchUsers(userQuery)); } catch (e) { say(e.message, 'error'); }
    };

    const qb = overview && overview.quickbooks;
    const lastRunOf = (jobKey) => (overview && overview.lastRuns || []).find(r => r.jobKey === jobKey);

    return (
        <div className="aag-page">
            <div className="aag-header">
                <div>
                    <h1>Accounts Agent</h1>
                    <p className="aag-subtitle">
                        Prepares the company's accounting work from QuickBooks and Stripe. Anything that sends,
                        posts or files waits here for an approver.
                    </p>
                </div>
                <Button variant="success" onClick={() => { loadOverview(); if (tab === 'runs') loadRuns(); if (tab === 'approvals') loadActions(); }}>
                    <RefreshCw className="icon" /> Refresh
                </Button>
            </div>

            <Tabs
                active={tab}
                onChange={setTab}
                tabs={[
                    { key: 'overview', label: 'Overview' },
                    { key: 'approvals', label: 'Approvals', count: overview ? overview.pendingActions : undefined },
                    { key: 'runs', label: 'Runs' },
                    { key: 'contracts', label: 'Contracts' },
                    { key: 'onboarding', label: 'Onboarding' },
                    { key: 'vat', label: 'VAT3' },
                    { key: 'ask', label: 'Ask' },
                    { key: 'settings', label: 'Settings' },
                ]}
            />

            {loading && !overview ? <div className="aag-loading"><Spinner /></div> : (
                <div className="aag-body">
                    {tab === 'overview' && overview && (
                        <>
                            {qb && !qb.connected && (
                                <div className="aag-warning">
                                    QuickBooks is not connected{qb.error ? ` (${qb.error})` : ''} — the reports and reconciliation need it.
                                    {isSuperAdmin && <> Connect it on the <button type="button" className="aag-link" onClick={() => setTab('settings')}>Settings</button> tab.</>}
                                </div>
                            )}
                            <table className="aag-table">
                                <thead><tr><th>Job</th><th>Mode</th><th>Last run</th><th>Result</th><th /></tr></thead>
                                <tbody>
                                    {overview.jobs.map(j => {
                                        const last = lastRunOf(j.jobKey);
                                        return (
                                            <tr key={j.jobKey}>
                                                <td><strong>{j.name}</strong><div className="aag-muted">{j.description}</div></td>
                                                <td>{j.enabled === 'Y' ? (j.mode === 'auto_under_limit' ? `Automatic under ${money(j.autoLimit)}` : 'Approve all') : 'Off'}</td>
                                                <td>{last ? <button type="button" className="aag-link" onClick={() => openRunDetail(last.id)}>{formatDateTime(last.startedAt)}</button> : '—'}</td>
                                                <td>{last ? <><StatusChip status={last.status} /> <span className="aag-muted">{last.summary}</span></> : '—'}</td>
                                                <td><Button variant="secondary" onClick={() => runNow(j.jobKey)} disabled={j.enabled !== 'Y'}><Play className="icon" /> Run now</Button></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </>
                    )}

                    {tab === 'approvals' && (
                        <>
                            <div className="aag-toolbar">
                                <select value={actionStatus} onChange={e => setActionStatus(e.target.value)}>
                                    {['PROPOSED', 'APPROVED', 'REJECTED', 'EXECUTED', 'FAILED', 'ALL'].map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                                {!isApprover && <span className="aag-muted">You can see the queue; only an accounts approver can decide.</span>}
                            </div>
                            {actions.length === 0 ? <p className="aag-muted">Nothing {actionStatus === 'ALL' ? 'yet' : actionStatus.toLowerCase()}. Stripe postings, invoices and reminders appear here once their jobs are switched on.</p> : (
                                <table className="aag-table">
                                    <thead><tr><th>Proposed</th><th>Job</th><th>Action</th><th>Customer</th><th>Amount</th><th>Why</th><th>Status</th><th /></tr></thead>
                                    <tbody>
                                        {actions.map(a => (
                                            <tr key={a.id}>
                                                <td>{formatDateTime(a.createdAt)}</td>
                                                <td>{a.jobKey}</td>
                                                <td>{a.actionType}</td>
                                                <td>{a.customerName || '—'}</td>
                                                <td>{money(a.amount, a.currency)}</td>
                                                <td className="aag-wrap">{a.reasoning}{a.payload && <details><summary>Exactly what will be done</summary><pre className="aag-pre">{JSON.stringify(a.payload, null, 2)}</pre></details>}</td>
                                                <td><StatusChip status={a.status} />{a.decidedBy && <div className="aag-muted">{a.decidedBy}</div>}</td>
                                                <td className="aag-actions">
                                                    {a.status === 'PROPOSED' && isApprover && (
                                                        <>
                                                            <Button variant="success" onClick={() => decide(a, true)}><Check className="icon" /> Approve</Button>
                                                            <Button variant="secondary" onClick={() => decide(a, false)}><X className="icon" /> Reject</Button>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </>
                    )}

                    {tab === 'runs' && (
                        <table className="aag-table">
                            <thead><tr><th>Started</th><th>Job</th><th>Trigger</th><th>Status</th><th>Summary</th></tr></thead>
                            <tbody>
                                {runs.map(r => (
                                    <tr key={r.id} className="aag-clickable" onClick={() => openRunDetail(r.id)}>
                                        <td>{formatDateTime(r.startedAt)}</td>
                                        <td>{r.jobKey}</td>
                                        <td>{r.triggerType}</td>
                                        <td><StatusChip status={r.status} /></td>
                                        <td className="aag-wrap">{r.error || r.summary}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {tab === 'contracts' && (
                        <>
                            <div className="aag-toolbar">
                                <p className="aag-muted" style={{ margin: 0, flex: 1 }}>What each customer is billed. When an invoice falls due it is proposed for approval, created in QuickBooks, then emailed by the Send invoices job.</p>
                                {(isApprover || isSuperAdmin) && <Button variant="primary" onClick={() => openContractForm(null)}>New contract</Button>}
                            </div>
                            {contractForm && (
                                <section className="aag-section aag-form">
                                    <h3>{contractForm.id ? `Contract ${contractForm.id}` : 'New contract'}</h3>
                                    <label>Customer
                                        {contractForm.qboCustomerId
                                            ? <div>{contractForm.customerName || contractForm.qboCustomerId} {!contractForm.id && <button type="button" className="aag-link" onClick={() => setContractForm(f => ({ ...f, qboCustomerId: '', customerName: '' }))}>change</button>}</div>
                                            : <div className="aag-toolbar">
                                                <input value={customerQuery} onChange={e => setCustomerQuery(e.target.value)} placeholder="Find a QuickBooks customer" onKeyDown={async e => { if (e.key === 'Enter') { try { setCustomerHits(await accountsAgentService.qboCustomers(customerQuery)); } catch (er) { say(er.message, 'error'); } } }} />
                                                <Button variant="secondary" onClick={async () => { try { setCustomerHits(await accountsAgentService.qboCustomers(customerQuery)); } catch (er) { say(er.message, 'error'); } }}>Find</Button>
                                              </div>}
                                        {!contractForm.qboCustomerId && customerHits.length > 0 && (
                                            <ul className="aag-list">{customerHits.map(c => (
                                                <li key={c.id}>{c.name} <span className="aag-muted">{c.email} {c.country}</span>
                                                    <Button variant="secondary" onClick={() => { setContractForm(f => ({ ...f, qboCustomerId: c.id, customerName: c.name })); setCustomerHits([]); }}>Choose</Button></li>
                                            ))}</ul>
                                        )}
                                    </label>
                                    <label>Title<input value={contractForm.title} onChange={e => setContractForm(f => ({ ...f, title: e.target.value }))} /></label>
                                    <label>Product / service (QuickBooks item)
                                        <select value={contractForm.qboItemId} onChange={e => setContractForm(f => ({ ...f, qboItemId: e.target.value }))}>
                                            <option value="">— choose —</option>
                                            {qboItems.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                                        </select>
                                    </label>
                                    <label>Invoice line description<input value={contractForm.description || ''} onChange={e => setContractForm(f => ({ ...f, description: e.target.value }))} /></label>
                                    <div className="aag-form-row">
                                        <label>Amount per invoice (net)<input type="number" min="0" step="0.01" value={contractForm.amount} onChange={e => setContractForm(f => ({ ...f, amount: e.target.value }))} /></label>
                                        <label>Currency<input value={contractForm.currency} maxLength={3} onChange={e => setContractForm(f => ({ ...f, currency: e.target.value.toUpperCase() }))} /></label>
                                        <label>Frequency
                                            <select value={contractForm.frequency} onChange={e => setContractForm(f => ({ ...f, frequency: e.target.value }))}>
                                                <option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="annual">Annual</option><option value="one_off">One-off</option>
                                            </select>
                                        </label>
                                    </div>
                                    <div className="aag-form-row">
                                        <label>Start<input type="date" value={contractForm.startDate || ''} onChange={e => setContractForm(f => ({ ...f, startDate: e.target.value }))} /></label>
                                        <label>End (blank = open-ended)<input type="date" value={contractForm.endDate || ''} onChange={e => setContractForm(f => ({ ...f, endDate: e.target.value }))} /></label>
                                        {contractForm.id && <label>Next invoice<input type="date" value={contractForm.nextInvoiceDate || ''} onChange={e => setContractForm(f => ({ ...f, nextInvoiceDate: e.target.value }))} /></label>}
                                    </div>
                                    <div className="aag-form-row">
                                        <label>Payment terms (days)<input type="number" min="0" value={contractForm.paymentTermsDays} onChange={e => setContractForm(f => ({ ...f, paymentTermsDays: e.target.value }))} /></label>
                                        <label>Renewal notice (days)<input type="number" min="0" value={contractForm.renewalNoticeDays} onChange={e => setContractForm(f => ({ ...f, renewalNoticeDays: e.target.value }))} /></label>
                                        {contractForm.id && <label>Status<select value={contractForm.status} onChange={e => setContractForm(f => ({ ...f, status: e.target.value }))}><option value="ACTIVE">Active</option><option value="ENDED">Ended</option></select></label>}
                                    </div>
                                    <div className="aag-toolbar">
                                        <Button variant="primary" onClick={saveContract}>Save</Button>
                                        <Button variant="secondary" onClick={() => setContractForm(null)}>Cancel</Button>
                                    </div>
                                </section>
                            )}
                            <table className="aag-table">
                                <thead><tr><th>Customer</th><th>Contract</th><th>Amount</th><th>Frequency</th><th>Period</th><th>Next invoice</th><th>Status</th></tr></thead>
                                <tbody>
                                    {contracts.map(c => (
                                        <tr key={c.id} className="aag-clickable" onClick={() => (isApprover || isSuperAdmin) && openContractForm(c)}>
                                            <td>{c.customerName || c.qboCustomerId}</td><td>{c.title}</td><td>{money(c.amount, c.currency)}</td><td>{c.frequency}</td>
                                            <td>{c.startDate} → {c.endDate || 'open'}</td><td>{c.nextInvoiceDate || '—'}</td><td><StatusChip status={c.status} /></td>
                                        </tr>
                                    ))}
                                    {contracts.length === 0 && <tr><td colSpan={7} className="aag-muted">No contracts yet.</td></tr>}
                                </tbody>
                            </table>
                        </>
                    )}

                    {tab === 'onboarding' && (
                        <>
                            <div className="aag-toolbar">
                                <p className="aag-muted" style={{ margin: 0, flex: 1 }}>A new customer: their EU VAT number is checked against VIES, then creating them in QuickBooks and Stripe goes to the approval queue — a new customer is never automatic.</p>
                                {(isApprover || isSuperAdmin) && <Button variant="primary" onClick={() => setOnboardingForm({ ...EMPTY_ONBOARDING })}>New customer</Button>}
                            </div>
                            {onboardingForm && (
                                <section className="aag-section aag-form">
                                    <h3>New customer</h3>
                                    <div className="aag-form-row">
                                        <label>Company name<input value={onboardingForm.companyName} onChange={e => setOnboardingForm(f => ({ ...f, companyName: e.target.value }))} /></label>
                                        <label>Billing email<input type="email" value={onboardingForm.billingEmail} onChange={e => setOnboardingForm(f => ({ ...f, billingEmail: e.target.value }))} /></label>
                                        <label>Contact<input value={onboardingForm.contactName} onChange={e => setOnboardingForm(f => ({ ...f, contactName: e.target.value }))} /></label>
                                    </div>
                                    <div className="aag-form-row">
                                        <label>Address<input value={onboardingForm.addressLine1} onChange={e => setOnboardingForm(f => ({ ...f, addressLine1: e.target.value }))} /></label>
                                        <label>Address line 2<input value={onboardingForm.addressLine2} onChange={e => setOnboardingForm(f => ({ ...f, addressLine2: e.target.value }))} /></label>
                                    </div>
                                    <div className="aag-form-row">
                                        <label>City<input value={onboardingForm.city} onChange={e => setOnboardingForm(f => ({ ...f, city: e.target.value }))} /></label>
                                        <label>Postcode<input value={onboardingForm.postalCode} onChange={e => setOnboardingForm(f => ({ ...f, postalCode: e.target.value }))} /></label>
                                        <label>Country (ISO code)<input maxLength={2} value={onboardingForm.countryCode} onChange={e => setOnboardingForm(f => ({ ...f, countryCode: e.target.value.toUpperCase() }))} /></label>
                                        <label>VAT number<input value={onboardingForm.vatNumber} onChange={e => setOnboardingForm(f => ({ ...f, vatNumber: e.target.value }))} /></label>
                                    </div>
                                    <div className="aag-toolbar">
                                        <Button variant="primary" onClick={async () => {
                                            try {
                                                const r = await accountsAgentService.createOnboarding(onboardingForm);
                                                say(r.vies && r.vies.checked ? `Saved — VAT number ${r.vies.valid ? 'valid' : 'NOT valid'} in VIES` : `Saved${r.vies && r.vies.reason ? ` — ${r.vies.reason}` : ''}`, r.vies && r.vies.checked && !r.vies.valid ? 'error' : 'success');
                                                setOnboardingForm(null); loadOnboarding();
                                            } catch (e) { say(e.response?.data?.error?.message || e.message, 'error'); }
                                        }}>Save and check VAT</Button>
                                        <Button variant="secondary" onClick={() => setOnboardingForm(null)}>Cancel</Button>
                                    </div>
                                </section>
                            )}
                            <table className="aag-table">
                                <thead><tr><th>Company</th><th>Billing email</th><th>Country</th><th>VAT (VIES)</th><th>Status</th><th /></tr></thead>
                                <tbody>
                                    {onboardingRows.map(o => (
                                        <tr key={o.id}>
                                            <td>{o.companyName}</td><td>{o.billingEmail}</td><td>{o.countryCode}</td>
                                            <td>{o.vatNumber ? <>{o.vatNumber} {o.viesValid === 'Y' ? <StatusChip status="VALID" /> : o.viesValid === 'N' ? <StatusChip status="INVALID" /> : <span className="aag-muted">not checked</span>}{o.viesName && <div className="aag-muted">{o.viesName}</div>}</> : '—'}</td>
                                            <td><StatusChip status={o.status} />{o.qboCustomerId && <div className="aag-muted">QuickBooks {o.qboCustomerId}</div>}</td>
                                            <td>{o.status === 'DRAFT' && (isApprover || isSuperAdmin) && (
                                                <Button variant="secondary" onClick={async () => { try { await accountsAgentService.proposeOnboarding(o.id); say('Sent to the approval queue'); loadOnboarding(); loadOverview(); } catch (e) { say(e.response?.data?.error?.message || e.message, 'error'); } }}>Propose creating</Button>
                                            )}</td>
                                        </tr>
                                    ))}
                                    {onboardingRows.length === 0 && <tr><td colSpan={6} className="aag-muted">No customers being onboarded.</td></tr>}
                                </tbody>
                            </table>
                        </>
                    )}

                    {tab === 'vat' && (
                        <>
                            <p className="aag-muted">Prepared from QuickBooks tax lines after each period ends. File it on ROS, then record the ROS reference here. Box values are whole euro, as entered on ROS.</p>
                            <table className="aag-table">
                                <thead><tr><th>Period</th><th>Status</th>{VAT_BOXES.map(b => <th key={b}>{b}</th>)}<th>Filed</th></tr></thead>
                                <tbody>
                                    {vatPeriods.map(v => (
                                        <tr key={v.id} className="aag-clickable" onClick={() => openVat(v.id)}>
                                            <td>{String(v.periodStart).slice(0, 10)} → {String(v.periodEnd).slice(0, 10)}</td>
                                            <td><StatusChip status={v.status} /></td>
                                            {VAT_BOXES.map(b => <td key={b}>{v.boxes && v.boxes[b] ? v.boxes[b].whole.toLocaleString('en-IE') : '—'}</td>)}
                                            <td>{v.rosReference ? <>{v.rosReference}<div className="aag-muted">{String(v.filedOn).slice(0, 10)} · {v.filedBy}</div></> : '—'}</td>
                                        </tr>
                                    ))}
                                    {vatPeriods.length === 0 && <tr><td colSpan={VAT_BOXES.length + 3} className="aag-muted">No returns prepared yet — switch on Prepare VAT3 and map the tax rates below.</td></tr>}
                                </tbody>
                            </table>

                            {vatOpen && (
                                <section className="aag-section">
                                    <h3>VAT3 {String(vatOpen.periodStart).slice(0, 10)} → {String(vatOpen.periodEnd).slice(0, 10)} <StatusChip status={vatOpen.status} /></h3>
                                    {vatOpen.commentary && <div className="aag-commentary">{vatOpen.commentary}</div>}
                                    {vatOpen.boxes && (
                                        <table className="aag-table">
                                            <thead><tr><th>Box</th><th>Whole euro (ROS)</th><th>Exact</th></tr></thead>
                                            <tbody>{VAT_BOXES.map(b => <tr key={b}><td>{b}</td><td>{vatOpen.boxes[b].whole.toLocaleString('en-IE')}</td><td>{money(vatOpen.boxes[b].eur)}</td></tr>)}</tbody>
                                        </table>
                                    )}
                                    {vatOpen.problems && vatOpen.problems.unmappedRates && vatOpen.problems.unmappedRates.length > 0 && (
                                        <div className="aag-warning">Not prepared — map these tax rates below first: {vatOpen.problems.unmappedRates.map(u => `${u.name} (${u.count})`).join(', ')}</div>
                                    )}
                                    {vatOpen.problems && vatOpen.problems.documentsWithoutTaxLinesCount > 0 && (
                                        <div className="aag-warning">{vatOpen.problems.documentsWithoutTaxLinesCount} document(s) have no tax lines and are not in any box — check they are meant to be outside VAT (listed in the run's figures).</div>
                                    )}
                                    <div className="aag-toolbar">
                                        <Button variant="secondary" onClick={() => downloadWorkingFile(vatOpen.id)}>Download working file (CSV)</Button>
                                    </div>
                                    {vatOpen.status === 'PREPARED' && isApprover && (
                                        <div className="aag-toolbar">
                                            <input placeholder="ROS reference" value={vatFiled.rosReference} onChange={e => setVatFiled(f => ({ ...f, rosReference: e.target.value }))} />
                                            <input type="date" value={vatFiled.filedOn} onChange={e => setVatFiled(f => ({ ...f, filedOn: e.target.value }))} />
                                            <Button variant="primary" disabled={!vatFiled.rosReference || !vatFiled.filedOn} onClick={() => setConfirm({
                                                title: 'Record this VAT3 as filed?', message: `Filed on ROS on ${vatFiled.filedOn}, reference ${vatFiled.rosReference}. The return will not be prepared again.`,
                                                onConfirm: async () => { setConfirm(null); try { await accountsAgentService.markVatFiled(vatOpen.id, vatFiled.rosReference, vatFiled.filedOn); say('Recorded as filed'); setVatOpen(null); loadVat(); } catch (e) { say(e.response?.data?.error?.message || e.message, 'error'); } },
                                            })}>Record as filed</Button>
                                        </div>
                                    )}
                                </section>
                            )}

                            <section className="aag-section">
                                <h3>Tax rates</h3>
                                <p className="aag-muted">Each QuickBooks tax rate's place on the VAT3. A period with a transaction at an unmapped rate is not prepared.</p>
                                {vatRates && vatRates.error && <div className="aag-warning">{vatRates.error}</div>}
                                {vatRates && vatRates.rates && (
                                    <>
                                        <table className="aag-table">
                                            <thead><tr><th>QuickBooks rate</th><th>%</th><th>On the VAT3 as</th></tr></thead>
                                            <tbody>
                                                {vatRates.rates.map(r => (
                                                    <tr key={r.id}>
                                                        <td>{r.name}{!r.active && <span className="aag-muted"> (inactive)</span>}</td>
                                                        <td>{r.percent}</td>
                                                        <td>
                                                            <select disabled={!isSuperAdmin} value={r.role || ''} onChange={e => setVatRates(v => ({ ...v, rates: v.rates.map(x => (x.id === r.id ? { ...x, role: e.target.value || null } : x)) }))}>
                                                                <option value="">— not mapped —</option>
                                                                {vatRates.roles.map(role => <option key={role} value={role}>{VAT_ROLE_LABELS[role] || role}</option>)}
                                                            </select>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {isSuperAdmin && (
                                            <Button variant="primary" className="sa-only" onClick={async () => {
                                                try { await accountsAgentService.saveVatRates(vatRates.rates.map(r => ({ id: r.id, name: r.name, role: r.role }))); say('Tax rates saved'); loadVat(); }
                                                catch (e) { say(e.response?.data?.error?.message || e.message, 'error'); }
                                            }}>Save tax rates</Button>
                                        )}
                                    </>
                                )}
                            </section>
                        </>
                    )}

                    {tab === 'ask' && (
                        <div className="aag-ask">
                            <p className="aag-muted">Ask about invoices, payments, customers, cash or revenue. The agent looks it up in QuickBooks and Stripe; it cannot change anything.</p>
                            <textarea rows={3} value={question} onChange={e => setQuestion(e.target.value)} placeholder="e.g. Which customers owe us more than €1,000 and for how long?" />
                            <Button variant="primary" onClick={ask} disabled={asking || !question.trim()}>
                                {asking ? <><Spinner size="sm" /> Looking it up…</> : <><Send className="icon" /> Ask</>}
                            </Button>
                            {answer && (
                                <div className="aag-commentary">
                                    {answer.status === 'SUCCEEDED' ? answer.output?.answer : <span className="aag-error">{answer.error}</span>}
                                    {answer.output?.lookups?.length > 0 && (
                                        <details><summary>What it looked up ({answer.output.lookups.length})</summary><pre className="aag-pre">{JSON.stringify(answer.output.lookups, null, 2)}</pre></details>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {tab === 'settings' && overview && (
                        <>
                            <section className="aag-section">
                                <h3>QuickBooks Online</h3>
                                {qb && qb.connected
                                    ? <p>Connected to company <strong>{qb.realmId}</strong> ({qb.environment}) since {formatDateTime(qb.connectedAt)}. The connection must be renewed before {formatDateTime(qb.refreshTokenExpiresAt)}.</p>
                                    : <p className="aag-muted">{qb && !qb.configured ? 'The QuickBooks app keys are not in the accounts QuickBooks secret yet.' : 'Not connected.'}</p>}
                                {isSuperAdmin && <Button variant="secondary" className="sa-only" onClick={connectQuickBooks}><Link2 className="icon" /> {qb && qb.connected ? 'Reconnect' : 'Connect'} QuickBooks</Button>}
                            </section>

                            <section className="aag-section">
                                <h3>Posting and email</h3>
                                <p className="aag-muted">Where Stripe payouts are posted in QuickBooks, and who is copied on reminders. A job that needs one of these will not run until it is set.</p>
                                <table className="aag-table">
                                    <tbody>
                                        {settingsRows.map(r => (
                                            <tr key={r.settingKey}>
                                                <td><strong>{r.label}</strong><div className="aag-muted">{r.help}</div></td>
                                                <td style={{ minWidth: '18rem' }}>
                                                    {r.settingKey.startsWith('qbo_') && r.settingKey.endsWith('_account_id') ? (
                                                        <select disabled={!isSuperAdmin} value={settingsDraft[r.settingKey] || ''} onChange={e => setSettingsDraft(d => ({ ...d, [r.settingKey]: e.target.value }))}>
                                                            <option value="">{qboAccounts.length ? '— choose an account —' : 'Connect QuickBooks to choose'}</option>
                                                            {qboAccounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.type})</option>)}
                                                            {settingsDraft[r.settingKey] && !qboAccounts.some(a => a.id === settingsDraft[r.settingKey]) && (
                                                                <option value={settingsDraft[r.settingKey]}>Account {settingsDraft[r.settingKey]}</option>
                                                            )}
                                                        </select>
                                                    ) : (
                                                        <input disabled={!isSuperAdmin} value={settingsDraft[r.settingKey] || ''} onChange={e => setSettingsDraft(d => ({ ...d, [r.settingKey]: e.target.value }))} />
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {isSuperAdmin && (
                                    <Button variant="primary" className="sa-only" onClick={async () => {
                                        try { await accountsAgentService.saveSettings(settingsDraft); say('Settings saved'); loadSettings(); }
                                        catch (e) { say(e.response?.data?.error?.message || e.message, 'error'); }
                                    }}>Save settings</Button>
                                )}
                            </section>

                            <section className="aag-section">
                                <h3>Approvers</h3>
                                <p className="aag-muted">Only these people can approve or reject what the agent proposes.</p>
                                <ul className="aag-list">
                                    {approvers.map(a => (
                                        <li key={a.id}>
                                            {a.firstname} {a.lastname}
                                            {isSuperAdmin && (
                                                <button type="button" className="aag-icon-btn sa-only" aria-label="Remove approver"
                                                    onClick={() => setConfirm({ title: 'Remove approver?', message: `${a.firstname} ${a.lastname} will no longer be able to approve accounts actions.`, danger: true,
                                                        onConfirm: async () => { setConfirm(null); try { await accountsAgentService.removeApprover(a.id); say('Removed'); loadApprovers(); } catch (e) { say(e.message, 'error'); } } })}>
                                                    <Trash2 size={16} />
                                                </button>
                                            )}
                                        </li>
                                    ))}
                                    {approvers.length === 0 && <li className="aag-muted">No approvers yet.</li>}
                                </ul>
                                {isSuperAdmin && (
                                    <div className="aag-toolbar sa-only">
                                        <input value={userQuery} onChange={e => setUserQuery(e.target.value)} placeholder="Find a user by name or email" onKeyDown={e => e.key === 'Enter' && searchUsers()} />
                                        <Button variant="secondary" onClick={searchUsers}>Find</Button>
                                    </div>
                                )}
                                {userHits.length > 0 && (
                                    <ul className="aag-list">
                                        {userHits.map(u => (
                                            <li key={u.id}>
                                                {u.firstname} {u.lastname} <span className="aag-muted">{u.email}</span>
                                                <Button variant="secondary" onClick={async () => { try { await accountsAgentService.addApprover(u.id); say('Approver added'); setUserHits([]); setUserQuery(''); loadApprovers(); } catch (e) { say(e.message, 'error'); } }}>
                                                    <UserPlus className="icon" /> Add
                                                </Button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </section>

                            <section className="aag-section">
                                <h3>Jobs</h3>
                                <table className="aag-table">
                                    <thead><tr><th>Job</th><th>On</th><th>Mode</th><th>Automatic limit</th></tr></thead>
                                    <tbody>
                                        {overview.jobs.map(j => (
                                            <tr key={j.jobKey}>
                                                <td>{j.name}</td>
                                                <td><input type="checkbox" disabled={!isSuperAdmin} checked={j.enabled === 'Y'} onChange={e => setJob(j.jobKey, { enabled: e.target.checked ? 'Y' : 'N' })} /></td>
                                                <td>
                                                    <select disabled={!isSuperAdmin} value={j.mode} onChange={e => setJob(j.jobKey, { mode: e.target.value })}>
                                                        <option value="approve_all">Approve everything</option>
                                                        <option value="auto_under_limit">Automatic under the limit</option>
                                                    </select>
                                                </td>
                                                <td>
                                                    <input type="number" min="0" step="100" disabled={!isSuperAdmin} defaultValue={j.autoLimit}
                                                        onBlur={e => Number(e.target.value) !== Number(j.autoLimit) && setJob(j.jobKey, { autoLimit: Number(e.target.value) })} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <p className="aag-muted">Automatic actions are only ever for existing customers and never above the limit; everything else waits for an approver.</p>
                            </section>
                        </>
                    )}
                </div>
            )}

            {openRun && <RunDetail run={openRun} onClose={() => setOpenRun(null)} />}
            {confirm && <ConfirmModal title={confirm.title} message={confirm.message} danger={confirm.danger} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}
            {toast && <Toast message={toast.message} kind={toast.kind} onClose={() => setToast(null)} />}
        </div>
    );
}
